//services/CreateurService.js

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EventModel from "../models/EventModel";
import DashboardModel from "../models/DashboardModel";
import CreateurModel from "../models/CreateurModel";
import CategorieModel from "../models/CategorieModel"; // chemin vers ton modèle

import { base_url } from "../utils/constante";




// ============================================================
// RÉCUPÉRER UN ÉVÉNEMENT PUBLIC
// ============================================================

export async function getPublicEventById(id) {
    if (!id) {
        throw new Error("ID de l'événement manquant");
    }

    try {
        const response = await fetch(`${base_url}evenement/${id}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                // 🔑 Le web n'a pas de contrainte de review (contrairement à
                //    l'app mobile) : il se redéploie instantanément.
                //    On envoie toujours ce header pour recevoir le JSON brut
                //    des tarifs (pas le fallback texte prévu pour l'ancienne
                //    app mobile bloquée en review).
                "X-App-Client": "web-react",
            },
        });

        const text = await response.text();

        let data;

        try {
            data = JSON.parse(text);
        } catch (error) {
            console.error("Réponse API non JSON :", text);
            throw new Error("Réponse invalide du serveur");
        }

        if (!response.ok) {
            throw new Error(
                data?.message ||
                `Erreur API ${response.status}`
            );
        }

        /*
         * Selon la réponse de ton backend, on accepte :
         *
         * { event: {...} }
         * { data: {...} }
         * { ...event }
         */

        const event =
            data?.event ||
            data?.data ||
            data;

        if (!event || typeof event !== "object") {
            throw new Error("Événement introuvable");
        }

        return event;

    } catch (error) {
        console.error("Erreur getPublicEventById :", error);
        throw error;
    }
}



export function useGetEvents(initialSort = "date", initialOrder = "desc") {
    const [events, setEvents] = useState(null); // null au début
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1); // page courante
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 9,
        current_page: 1,
        last_page: 1,
    });

    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [sort, setSort] = useState(initialSort);
    const [order, setOrder] = useState(initialOrder);

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const navigate = useNavigate();

    // 🔹 Fonction fetchEvents
    const fetchEvents = useCallback(
        async (pageToLoad = 1) => {
            if (!token) {
                navigate("/devenir-partenaire");
                return;
            }

            setLoading(true);

            try {
                const response = await fetch(`${base_url}createur/myEvents`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        page: pageToLoad,
                        search: search || null,
                        start_date: startDate ? startDate.toISOString().split("T")[0] : null,
                        end_date: endDate ? endDate.toISOString().split("T")[0] : null,
                        sort,
                        order,
                    }),
                });

                if (!response.ok) throw new Error("Erreur lors de la récupération des événements");

                const data = await response.json();
                const formatted = data.events.map((item) => new EventModel(item));

                setEvents(formatted);
                setPagination(data.pagination);
                setPage(pageToLoad); // ⚡ met à jour le state de la page
            } catch (error) {
                console.error("Erreur:", error);
                setEvents([]);
            } finally {
                setLoading(false);
            }
        },
        [token, navigate, search, startDate, endDate, sort, order] // ✅ dépendances correctes
    );

    // 🔹 Rechargement automatique quand les filtres changent
    useEffect(() => {
        fetchEvents(page);
    }, [fetchEvents, page]);

    return {
        events,
        loading,
        pagination,
        page,
        setPage,
        search,
        setSearch,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        sort,
        setSort,
        order,
        setOrder,
        fetchEvents,
        setEvents,
    };
}
export async function getCategories() {
    try {
        const response = await fetch(`${base_url}getCategorieCreateur`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error("Erreur lors de la récupération des catégories");
        }

        const result = await response.json();

        // Vérifie que result.data est bien un tableau
        if (!Array.isArray(result.data)) {
            throw new Error("Format de données inattendu");
        }

        // Mappe chaque élément en instance de CategorieModel
        const categories = result.data.map(item => new CategorieModel(item));

        // 🔥 console log pour debug
        //console.log("Catégories reçues du serveur :", categories);

        return categories;
    } catch (err) {
        //console.error("Erreur getCategories :", err);
        return [];
    }
}

export async function getDashboard(token) {
    if (!token) throw new Error("Utilisateur non authentifié");

    try {
        const response = await fetch(`${base_url}createur/dashboard`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error("Erreur lors de la récupération du dashboard");
        }

        const data = await response.json();
        console.log("Données du dashboard reçues :", data);
        return new DashboardModel(data);
    } catch (err) {
        console.error("Erreur getDashboard:", err);
        return null;
    }
}
export async function addEvent(formData, token) {
    if (!token) throw new Error("Utilisateur non authentifié");

    try {
        const response = await fetch(`${base_url}createur/addEvent`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                // NE SURTOUT PAS mettre Content-Type ici, le navigateur le met tout seul !
            },
            body: formData,
        });

        if (!response.ok) {
            //const errorText = await response.text();
            //console.error("Erreur API :", errorText);
            throw new Error("Erreur lors de l'ajout de l'événement");
        }

        const data = await response.json();
        //console.log("SUCCESS addEvent :", data);

        return data;
    } catch (err) {
        console.error("Erreur addEvent:", err);
        throw err;
    }
}

export async function toggleEventStatus(eventId, token) {
    if (!token) throw new Error("Utilisateur non authentifié");
    if (!eventId) throw new Error("ID de l'événement manquant");

    try {
        const response = await fetch(`${base_url}createur/toggleEventStatus`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ id: eventId }),
        });

        if (!response.ok) {
            //const errorText = await response.text();
            //console.error("Erreur API toggleEventStatus :", errorText);
            throw new Error("Erreur lors du changement de statut de l'événement");
        }

        const data = await response.json();
        //console.log("toggleEventStatus succès :", data);
        return data; // { message: "Événement activé/désactivé", is_active: true/false }
    } catch (err) {
        //console.error("Erreur toggleEventStatus :", err);
        throw err;
    }
}

// Récupérer un événement par ID
export async function getEventById(id, token) {
    //console.log("🚀 getEventById appelé avec id:", id);

    try {
        const url = `${base_url}createur/geteventById`;
        //console.log("🔗 URL API :", url);
        //console.log("🔐 Token :", token);

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ id }),
        });

        //console.log("📡 Response HTTP status:", response.status);
        //console.log("📡 Response headers:", response.headers);

        const text = await response.text(); // on lit le texte brut d'abord
        //console.log("📄 Response texte brut :", text);

        // Essayons de parser le JSON
        let data;
        try {
            data = JSON.parse(text);
            //console.log("✅ JSON parsé :", data);
        } catch (jsonErr) {
            //console.error("❌ Erreur parsing JSON :", jsonErr, "Texte reçu :", text);
            throw jsonErr;
        }

        // Vérifie que l'objet event existe
        if (!data.event) {
            //console.warn("⚠️ Aucun événement trouvé dans la réponse");
            throw new Error("Aucun événement trouvé dans la réponse");
        }

        //console.log("🎯 Event récupéré :", data.event);
        return data.event;

    } catch (err) {
        //console.error("🔥 Erreur getEventById :", err);
        throw err;
    }
}



export async function updateEvent(formData, token) {
    if (!token) throw new Error("Utilisateur non authentifié");

    try {
        const response = await fetch(`${base_url}createur/updateEvent`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            //const errorText = await response.text();
            //console.error("Erreur API :", errorText);
            throw new Error("Erreur lors de la mise à jour de l'événement");
        }

        const data = await response.json();
        //console.log("SUCCESS updateEvent :", data);
        return data;
    } catch (err) {
        //console.error("Erreur updateEvent:", err);
        throw err;
    }
}


//tu dois creer ici fonction  

// 🔹 1) Récupérer le profil du créateur
export async function createurGetMyProfil(token) {
    if (!token) throw new Error("Utilisateur non authentifié");

    try {
        const response = await fetch(`${base_url}createur/getmyprofil`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error("Erreur lors de la récupération du profil : " + errorText);
        }

        const data = await response.json();
        //console.log("Profil créateur reçu :", data);
        //console.log("Profil créateur reçu (data.user) :", data.user);
        return new CreateurModel(data.user); // transforme en model
    } catch (err) {
        //console.error("Erreur createurGetMyProfil:", err);
        return null;
    }
}

// 🔹 2) Mettre à jour les infos du profil (hors image et mot de passe)
export async function createurUpdateProfile(formData, token) {
    if (!token) throw new Error("Utilisateur non authentifié");
    console.log("Données envoyées à l'API createurUpdateProfile :", formData);
    try {
        const response = await fetch(`${base_url}createur/profile/updateinfo`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
        });

        const data = await response.json(); // parser JSON

        if (!response.ok) {
            // si validation Laravel, data.errors contiendra les détails
            const backendMessage = data.message || (data.errors ? JSON.stringify(data.errors) : "Erreur lors de la mise à jour du profil");
            throw new Error(backendMessage);
        }

        return new CreateurModel(data.user);
    } catch (err) {
        throw err;
    }
}


// 🔹 3) Mettre à jour l'image de profil
export async function createurUpdateImage(imageFile, token) {
    if (!token) throw new Error("Utilisateur non authentifié");

    const formData = new FormData();
    formData.append("image", imageFile);

    try {
        const response = await fetch(`${base_url}createur/profile/updateimage`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        const data = await response.json(); // toujours parser en JSON

        if (!response.ok) {
            // Affiche le message envoyé par le backend si disponible
            const backendMessage = data.message || "Erreur lors de la mise à jour de l’image";
            throw new Error(backendMessage);
        }

        // Retourne directement l'URL de l'image ou ton modèle
        return data;
    } catch (err) {
        console.error("Erreur createurUpdateImage:", err.message);
        throw err;
    }
}


// 🔹 4) Mettre à jour le mot de passe
export async function createurUpdatePassword(oldPassword, newPassword, confirmPassword, token) {
    if (!token) throw new Error("Utilisateur non authentifié");

    try {
        const response = await fetch(`${base_url}createur/profile/password`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                old_password: oldPassword,
                new_password: newPassword,
                new_password_confirmation: confirmPassword
            }),
        });

        const data = await response.json(); // parser le JSON

        if (!response.ok) {
            // Affiche le message exact envoyé par le backend
            const backendMessage = data.message || "Erreur lors de la mise à jour du mot de passe";
            //console.error("Erreur API createurUpdatePassword :", backendMessage);
            throw new Error(backendMessage);
        }

        //console.log("SUCCESS createurUpdatePassword :", data);

        return data;
    } catch (err) {
        //console.error("Erreur createurUpdatePassword:", err.message);
        throw err;
    }
}

// 🔹 5) Mettre à jour l'image de profil
export async function createurUpdateBackground(imageFile, token) {

    if (!token) throw new Error("Utilisateur non authentifié");

    const formData = new FormData();

    formData.append("image", imageFile);

    const response = await fetch(`${base_url}createur/profile/updatebackground`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: formData
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || "Erreur upload background");
    }

    return data;
}